import {
  Box,
  CurrentDocumentProvider,
  DROPDOWN_PORTAL_ID,
  MODAL_PORTAL_ID,
  POPOVER_PORTAL_ID,
  PortalContextProvider,
  usePortal,
} from '@talos/kyoko';
import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ErrorBoundary } from '../ErrorBoundary';
import { POPOUT_URL } from '../Layout/tokens';
import NewWindow from '../NewWindow';
import { PopoutStyleProvider } from './PopoutStyleProvider';

interface PopoutWindowProps {
  /** Window name identifier */
  name: string;
  /** Window title displayed in the title bar */
  title: string;
  /** Callback when the window is closed */
  onClose: () => void;
  /** Callback when the window is opened */
  onOpen?: (window: Window) => void;
  /** Window width in pixels */
  width: number;
  /** Window height in pixels */
  height: number;
  /** Window left position in pixels */
  left: number;
  /** Window top position in pixels */
  top: number;
  /** Content to render inside the popout window */
  children: ReactNode;
}

/**
 * A generic popout window component that handles the common pattern of wrapping content
 * in ErrorBoundary, PortalContextProvider, NewWindow, and CurrentDocumentProvider.
 * Includes portal targets for modals, popovers, and dropdowns.
 */
export const PopoutWindow = memo(function PopoutWindow(props: PopoutWindowProps) {
  // PortalContextProvider must be rendered before usePortal is called (in PopoutWindowInner),
  // so the portal refs are registered in the popout's own context rather than the ambient one.
  return (
    <ErrorBoundary>
      <PortalContextProvider>
        <PopoutWindowInner {...props} />
      </PortalContextProvider>
    </ErrorBoundary>
  );
});

function PopoutWindowInner({ name, title, onClose, onOpen, width, height, left, top, children }: PopoutWindowProps) {
  const [popoutWindow, setPopoutWindow] = useState<Window>();
  const { setPortalRef: modalPortalRef } = usePortal(MODAL_PORTAL_ID);
  const { setPortalRef: dropdownPortalRef } = usePortal(DROPDOWN_PORTAL_ID);
  const { setPortalRef: popoverPortalRef } = usePortal(POPOVER_PORTAL_ID);

  // Stable reference so NewWindow's effect does not re-run when the parent re-renders with equal values.
  const features = useMemo(() => ({ width, height, left, top }), [width, height, left, top]);

  // Close the OS window when this component unmounts (e.g. when the user docks back or closes chat).
  // The effect is keyed on the opened window: the initial run with undefined is a no-op, so
  // StrictMode's synthetic unmount/remount cycle is harmless. When the window IS open and this
  // component unmounts for real, we close it explicitly to avoid an orphaned window and poll interval.
  // OS-initiated close sets win.closed=true before the beforeunload fires, so the guard prevents
  // double-close when dockBack is already triggered by NewWindow's onUnload callback.
  useEffect(() => {
    const win = popoutWindow;
    if (win == null) {
      return;
    }
    return () => {
      if (!win.closed) {
        win.close();
      }
    };
  }, [popoutWindow]);

  const handleOpen = useCallback(
    (openedWindow: Window) => {
      setPopoutWindow(openedWindow);
      onOpen?.(openedWindow);

      openedWindow.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          openedWindow.close();
        }
        if (e.key === 'F5' || (e.metaKey && e.key === 'r')) {
          // Prevent default refresh behavior
          e.preventDefault();
        }
      });
    },
    [onOpen]
  );

  return (
    <NewWindow
      url={POPOUT_URL}
      name={name}
      features={features}
      onUnload={onClose}
      onBlock={onClose}
      onOpen={handleOpen}
      title={title}
    >
      {popoutWindow && (
        <CurrentDocumentProvider currentDocument={popoutWindow.document} currentWindow={popoutWindow}>
          {/* Popouts need to be wrapped with their own StyleSheetManager implementation */}
          <PopoutStyleProvider popoutWindow={popoutWindow}>
            {/* Portal targets for rendering components inside the popout window */}
            <Box ref={modalPortalRef} />
            <Box ref={popoverPortalRef} />
            <Box ref={dropdownPortalRef} />
            {children}
          </PopoutStyleProvider>
        </CurrentDocumentProvider>
      )}
    </NewWindow>
  );
}
